<script setup>
import { ref, onMounted, computed, watch } from 'vue'; // Import watch
import axios from 'axios';
import { nextTick } from 'vue'; // Import nextTick for notification
import { API_BASE_URL } from '@/config';

// --- Reactive State ---
// Master list of main safety instructions
const safetyInstructions = ref([]);
// Currently selected main safety instruction
const selectedInstruction = ref(null);
// Detail list of instruction items for the selected main instruction
const instructionItems = ref([]);

// Form state for adding/editing main instructions
const newInstruction = ref({ SafetyNo: '', SafetyName: '' });
const editingInstruction = ref(null); // Holds the instruction being edited

// Form state for adding/editing instruction items
const newItem = ref({ SafetyDesc: '' }); // Only need description for new item
const editingItem = ref(null); // Holds the instruction item being edited


// !! IMPORTANT: Replace with the actual base URL of your Node.js backend !!
// These URLs should match where your backend is running and where the routes are mounted.
const API_INSTRUCTIONS_URL = '${API_BASE_URL}/api/safetyinstructions'; // Base URL for main instructions
const API_ITEMS_URL = '${API_BASE_URL}/api/safetyinstructions/items'; // Base URL for instruction items (note the /items path)


const loading = ref(false); // Loading state for async operations
const error = ref(null); // Error state for displaying persistent error messages
const notification = ref({ // State for managing temporary notifications
  show: false,
  message: '',
  type: '' // 'success', 'error', 'warning'
});

// --- Computed Properties for v-model Binding ---
// Handles the Safety Number input for the main instruction form
const currentInstructionNo = computed({
  get() {
    return editingInstruction.value ? editingInstruction.value.SafetyNo : newInstruction.value.SafetyNo;
  },
  set(value) {
    if (editingInstruction.value) {
      editingInstruction.value.SafetyNo = value;
    } else {
      newInstruction.value.SafetyNo = value;
    }
  }
});

// Handles the Safety Name input for the main instruction form
const currentInstructionName = computed({
  get() {
    return editingInstruction.value ? editingInstruction.value.SafetyName : newInstruction.value.SafetyName;
  },
  set(value) {
    if (editingInstruction.value) {
      editingInstruction.value.SafetyName = value;
    } else {
      newInstruction.value.SafetyName = value;
    }
  }
});

// Handles the Instruction Description input for the item form
const currentItemDesc = computed({
    get() {
        return editingItem.value ? editingItem.value.SafetyDesc : newItem.value.SafetyDesc;
    },
    set(value) {
        if (editingItem.value) {
            editingItem.value.SafetyDesc = value;
        } else {
            newItem.value.SafetyDesc = value;
        }
    }
});

// --- End Computed Properties ---


// --- Master (Main Instruction) CRUD Operations ---

// Fetches all main safety instructions
const fetchSafetyInstructions = async () => {
  loading.value = true;
  error.value = null;
  selectedInstruction.value = null; // Deselect any selected instruction
  instructionItems.value = []; // Clear items when fetching main list
  try {
    const response = await axios.get(API_INSTRUCTIONS_URL);
    safetyInstructions.value = response.data;
  } catch (err) {
    console.error('Error fetching main safety instructions:', err);
    error.value = 'Failed to fetch safety instructions. Please try again later.';
    showNotification(error.value, 'error');
  } finally {
    loading.value = false;
  }
};

// Creates a new main safety instruction
const createSafetyInstruction = async () => {
  if (!newInstruction.value.SafetyNo.trim() || !newInstruction.value.SafetyName.trim()) {
    showNotification('Safety Number and Name are required.', 'warning');
    return;
  }
  loading.value = true;
  error.value = null;
  try {
    const response = await axios.post(API_INSTRUCTIONS_URL, newInstruction.value);
    // Add the newly created instruction (with its generated ID) to the list
    safetyInstructions.value.push(response.data);
    newInstruction.value = { SafetyNo: '', SafetyName: '' }; // Clear form
    showNotification('Safety instruction created successfully!', 'success');
  } catch (err) {
    console.error('Error creating safety instruction:', err);
    if (err.response?.status === 409) {
      error.value = 'Safety Number already exists.';
    } else {
      error.value = err.response?.data || 'Failed to create safety instruction.';
    }
    showNotification(error.value, 'error');
  } finally {
    loading.value = false;
  }
};

// Updates a main safety instruction
const updateSafetyInstruction = async () => {
   if (!editingInstruction.value || !selectedInstruction.value) {
       showNotification('No safety instruction selected for editing.', 'warning');
       return;
   }
  if (!editingInstruction.value.SafetyName.trim()) {
    showNotification('Safety Name is required.', 'warning');
    return;
  }

  loading.value = true;
  error.value = null;
  try {
    // Send only updateable fields
    const payload = { SafetyName: editingInstruction.value.SafetyName };
    // Use SafetyNo from the original selected instruction for the PUT request URL
    const response = await axios.put(`${API_INSTRUCTIONS_URL}/${selectedInstruction.value.SafetyNo}`, payload);

    const index = safetyInstructions.value.findIndex(inst => inst.SafetyNo === selectedInstruction.value.SafetyNo);
    if (index !== -1) {
      // Update the instruction in the list with the data from the response
       safetyInstructions.value[index] = { ...safetyInstructions.value[index], ...response.data };
    }
    // Update the selected instruction if it was the one being edited
    if (selectedInstruction.value && selectedInstruction.value.SafetyNo === editingInstruction.value.SafetyNo) {
         selectedInstruction.value = { ...selectedInstruction.value, ...response.data };
    }


    editingInstruction.value = null; // Exit editing mode
    showNotification('Safety instruction updated successfully!', 'success');
  } catch (err) {
    console.error('Error updating safety instruction:', err);
    if (err.response?.status === 404) {
      error.value = 'Safety instruction not found.';
    } else {
      error.value = err.response?.data || 'Failed to update safety instruction.';
    }
    showNotification(error.value, 'error');
  } finally {
    loading.value = false;
  }
};

// Deletes a main safety instruction and its associated items
const deleteSafetyInstruction = async () => {
    if (!selectedInstruction.value) {
        showNotification('Please select a safety instruction to delete.', 'warning');
        return;
    }
  const safetyNoToDelete = selectedInstruction.value.SafetyNo;

  if (confirm(`Are you sure you want to delete safety instruction ${safetyNoToDelete}? This will also delete all associated instruction items.`)) {
    loading.value = true;
    error.value = null;
    try {
      // Backend is designed to delete items first due to FK
      await axios.delete(`${API_INSTRUCTIONS_URL}/${safetyNoToDelete}`);

      safetyInstructions.value = safetyInstructions.value.filter(inst => inst.SafetyNo !== safetyNoToDelete);
      // Clear selected and editing states if they match the deleted instruction
      selectedInstruction.value = null;
      editingInstruction.value = null;
      instructionItems.value = []; // Clear items list

      showNotification('Safety instruction deleted successfully!', 'success');
    } catch (err) {
      console.error('Error deleting safety instruction:', err);
      if (err.response?.status === 404) {
        error.value = 'Safety instruction not found.';
      } else if (err.response?.status === 409) {
           error.value = err.response.data || 'Conflict: Cannot delete safety instruction because it is referenced by other entities.';
      }
      else {
        error.value = 'Failed to delete safety instruction.';
      }
      showNotification(error.value, 'error');
    } finally {
      loading.value = false;
    }
  }
};

// --- Detail (Instruction Item) CRUD Operations ---

// Fetches instruction items for the currently selected main instruction
const fetchInstructionItems = async (safetyNo) => {
    if (!safetyNo) {
        instructionItems.value = []; // Clear items if no instruction is selected
        return;
    }
    loading.value = true;
    error.value = null;
    editingItem.value = null; // Clear item editing state
    newItem.value = { SafetyDesc: '' }; // Clear new item form
    try {
        // Use the specific endpoint for fetching items by SafetyNo
        const response = await axios.get(`${API_ITEMS_URL}/${safetyNo}`);
        instructionItems.value = response.data;
    } catch (err) {
        console.error(`Error fetching instruction items for SafetyNo ${safetyNo}:`, err);
        error.value = 'Failed to fetch instruction items.';
        showNotification(error.value, 'error');
    } finally {
        loading.value = false;
    }
};

// Creates a new instruction item for the selected main instruction
const createInstructionItem = async () => {
    if (!selectedInstruction.value) {
        showNotification('Please select a main safety instruction first.', 'warning');
        return;
    }
    if (!newItem.value.SafetyDesc.trim()) {
        showNotification('Instruction Description is required.', 'warning');
        return;
    }

    loading.value = true;
    error.value = null;
    try {
        // Send the new item data to the backend, including the SafetyNo from the selected instruction
        const response = await axios.post(`${API_ITEMS_URL}/${selectedInstruction.value.SafetyNo}`, newItem.value);

        // Add the newly created item (with its generated ID and Seq) to the local list
        // The backend is responsible for calculating SafetySeq and returning the full item
        instructionItems.value.push(response.data);
        // Sort items by SafetySeq to maintain order in the UI
        instructionItems.value.sort((a, b) => a.SafetySeq - b.SafetySeq);

        newItem.value = { SafetyDesc: '' }; // Clear form
        showNotification('Instruction item added successfully!', 'success');
    } catch (err) {
        console.error('Error creating instruction item:', err);
        error.value = err.response?.data || 'Failed to add instruction item.';
        showNotification(error.value, 'error');
    } finally {
        loading.value = false;
    }
};

// Starts editing an instruction item
const startEditingItem = (item) => {
    editingItem.value = { ...item }; // Copy item data to editing state
    newItem.value = { SafetyDesc: '' }; // Clear new item form
    error.value = null; // Clear errors
}

// Cancels editing an instruction item
const cancelEditingItem = () => {
    editingItem.value = null; // Clear editing state
    newItem.value = { SafetyDesc: '' }; // Clear new item form
    error.value = null; // Clear errors
}


// Updates an instruction item
const updateInstructionItem = async () => {
    if (!editingItem.value) {
        showNotification('No instruction item selected for editing.', 'warning');
        return;
    }
    if (!editingItem.value.SafetyDesc.trim()) {
        showNotification('Instruction Description is required.', 'warning');
        return;
    }

    loading.value = true;
    error.value = null;
    try {
        // Send only updateable fields (SafetyDesc)
        const payload = { SafetyDesc: editingItem.value.SafetyDesc };
        // Use the item's unique Id for the PUT request URL
        const response = await axios.put(`${API_INSTRUCTIONS_URL}/item/${editingItem.value.Id}`, payload); // Corrected URL path

        // Find the index of the updated item in the local list by its Id
        const index = instructionItems.value.findIndex(item => item.Id === editingItem.value.Id);
        if (index !== -1) {
            // Update the item in the list with the data from the response
            instructionItems.value[index] = { ...instructionItems.value[index], ...response.data };
             // Re-sort the list in case SafetySeq was somehow affected (though backend shouldn't allow this via PUT)
             instructionItems.value.sort((a, b) => a.SafetySeq - b.SafetySeq);
        }

        editingItem.value = null; // Exit editing mode
        showNotification('Instruction item updated successfully!', 'success');
    } catch (err) {
        console.error('Error updating instruction item:', err);
        if (err.response?.status === 404) {
            error.value = 'Instruction item not found.';
        } else {
            error.value = err.response?.data || 'Failed to update instruction item.';
        }
        showNotification(error.value, 'error');
    } finally {
        loading.value = false;
    }
};

// Deletes an instruction item
const deleteInstructionItem = async (itemId) => {
     if (!itemId) {
         showNotification('No instruction item selected for deletion.', 'warning');
         return;
     }
     if (!selectedInstruction.value) {
          showNotification('No main safety instruction selected.', 'warning');
          return;
     }

    if (confirm(`Are you sure you want to delete this instruction item?`)) {
        loading.value = true;
        error.value = null;
        try {
            // Use the item's unique Id for the DELETE request URL
            await axios.delete(`${API_INSTRUCTIONS_URL}/item/${itemId}`); // Corrected URL path

            // Remove the item from the local list by its Id
            instructionItems.value = instructionItems.value.filter(item => item.Id !== itemId);
             // Re-fetch items after deletion to ensure sequence numbers are handled correctly by backend if needed,
             // or just rely on filter if backend doesn't re-sequence on delete.
             // For simplicity here, we'll just filter. Re-fetching might be safer depending on backend logic.
             // await fetchInstructionItems(selectedInstruction.value.SafetyNo); // Alternative: Re-fetch all items

            // If the deleted item was being edited, clear editing state
            if(editingItem.value && editingItem.value.Id === itemId) {
                 editingItem.value = null;
            }

            showNotification('Instruction item deleted successfully!', 'success');
        } catch (err) {
            console.error(`Error deleting instruction item with Id ${itemId}:`, err);
            if (err.response?.status === 404) {
                error.value = 'Instruction item not found.';
            } else if (err.response?.status === 409) {
                 error.value = err.response.data || 'Conflict: Cannot delete instruction item because it is referenced by other entities.';
            }
            else {
                error.value = 'Failed to delete instruction item.';
            }
            showNotification(error.value, 'error');
        } finally {
            loading.value = false;
        }
    }
};


// --- UI Actions ---

// Selects a main safety instruction from the table
const selectInstruction = (instruction) => {
    selectedInstruction.value = instruction;
    editingInstruction.value = null; // Exit main instruction editing mode
    newInstruction.value = { SafetyNo: '', SafetyName: '' }; // Clear new instruction form
    error.value = null; // Clear errors
    fetchInstructionItems(instruction.SafetyNo); // Fetch items for the selected instruction
}

// Resets the form to the state for adding a new main instruction
const addNewInstruction = () => {
    editingInstruction.value = null; // Ensure not in editing mode
    selectedInstruction.value = null; // Deselect any main instruction
    newInstruction.value = { SafetyNo: '', SafetyName: '' }; // Clear form for new entry
    instructionItems.value = []; // Clear items list
    error.value = null; // Clear errors
};

// Initiates the editing process for the currently selected main instruction
const startEditingInstruction = () => {
  if (selectedInstruction.value) {
       // Copy selected instruction data to the editing state
       editingInstruction.value = { ...selectedInstruction.value };
       newInstruction.value = { SafetyNo: '', SafetyName: '' }; // Clear new instruction form
       error.value = null; // Clear any previous errors
  } else {
       showNotification('Please select a main safety instruction to edit.', 'warning');
  }
};

// Cancels the current editing process for main instruction
const cancelEditingInstruction = () => {
  editingInstruction.value = null;
  newInstruction.value = { SafetyNo: '', SafetyName: '' }; // Clear new instruction form
  error.value = null;
};


// --- Watchers ---
// Watch for changes in selectedInstruction and fetch items when it changes
// This replaces calling fetchInstructionItems directly in selectInstruction
// watch(selectedInstruction, (newInstruction, oldInstruction) => {
//     if (newInstruction) {
//         fetchInstructionItems(newInstruction.SafetyNo);
//     } else {
//         instructionItems.value = []; // Clear items if no instruction is selected
//     }
// });
// NOTE: The direct call in selectInstruction is fine and perhaps clearer for this case.
// Keeping the watcher commented out as an alternative pattern if needed elsewhere.


// Helper function to show a temporary notification message
const showNotification = (message, type) => {
  notification.value.message = message;
  notification.value.type = type;
  notification.value.show = true;

   nextTick(() => {
        const notificationElement = document.querySelector('.notification');
        if (notificationElement) {
             notificationElement.classList.remove('fade-out');
             void notificationElement.offsetWidth;
            setTimeout(() => {
                 notificationElement.classList.add('fade-out');
                 notificationElement.addEventListener('transitionend', () => {
                      if (!notificationElement.classList.contains('fade-out')) return;
                       notification.value.show = false;
                 }, { once: true });
            }, 5000);
        } else {
             setTimeout(() => {
                 notification.value.show = false;
             }, 5500);
        }
   });
};


// --- Lifecycle Hook ---
// Fetch main safety instructions when the component is first mounted
onMounted(fetchSafetyInstructions);
</script>

<template>
    <div class="safety-instruction-page">
        <div class="container">
            <div class="master-panel">
                <h2>Safety</h2>

                <div v-if="loading && safetyInstructions.length === 0">Loading safety instructions...</div>
                <div v-if="error && safetyInstructions.length === 0" class="error-message">{{ error }}</div>

                <div class="table-actions">
                    <button @click="addNewInstruction" :disabled="loading">Add New</button>
                    <button @click="startEditingInstruction" :disabled="!selectedInstruction || loading">Edit</button>
                    <button @click="deleteSafetyInstruction" :disabled="!selectedInstruction || loading">Delete</button>
                    <button @click="fetchSafetyInstructions" :disabled="loading">Refresh</button>
                </div>

                <table class="site-table">
                    <thead>
                        <tr>
                            <th>Safety No</th>
                            <th>Safety Name</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="instruction in safetyInstructions" :key="instruction.SafetyNo"
                            :class="{ 'selected': selectedInstruction && selectedInstruction.SafetyNo === instruction.SafetyNo }"
                            @click="selectInstruction(instruction)">
                            <td>{{ instruction.SafetyNo }}</td>
                            <td>{{ instruction.SafetyName }}</td>
                        </tr>
                        <tr v-if="safetyInstructions.length === 0 && !loading">
                            <td colspan="2" style="text-align: center;">No safety instructions found.</td>
                        </tr>
                    </tbody>
                </table>

                <div class="form-panel master-form">
                    <h3>{{ editingInstruction ? 'Edit Instruction' : 'Add New Instruction' }}</h3>
                    <form @submit.prevent="editingInstruction ? updateSafetyInstruction() : createSafetyInstruction()">
                        <div class="form-group">
                            <label for="master_safety_no">Safety Number:</label>
                            <input type="text" id="master_safety_no" v-model="currentInstructionNo" required
                                :disabled="editingInstruction !== null || loading">
                        </div>
                        <div class="form-group">
                            <label for="master_safety_name">Safety Name:</label>
                            <input type="text" id="master_safety_name" v-model="currentInstructionName" required
                                :disabled="loading">
                        </div>
                        <div class="form-actions">
                            <button type="submit" class="save-button" :disabled="loading">
                                {{ editingInstruction ? 'Update Instruction' : 'Save Instruction' }}
                            </button>
                            <button type="button" class="cancel-button" @click="cancelEditingInstruction" v-if="editingInstruction"
                                :disabled="loading">
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <div class="detail-panel">
                <h2>Instruction Items</h2>

                <div v-if="!selectedInstruction">
                    <p style="text-align: center; color: #666;">Select a Safety Instruction to view/add items.</p>
                </div>

                 <div v-if="selectedInstruction">
                     <div v-if="loading && instructionItems.length === 0 && !error">Loading items...</div>
                     <div v-if="error && instructionItems.length === 0" class="error-message">{{ error }}</div>

                     <div class="form-panel detail-form">
                         <h3>{{ editingItem ? 'Edit Item' : 'Add New Item' }}</h3>
                          <form @submit.prevent="editingItem ? updateInstructionItem() : createInstructionItem()">
                             <div class="form-group">
                                 <label for="item_desc">Instruction Description:</label>
                                 <input type="text" id="item_desc" v-model="currentItemDesc" required
                                        :disabled="loading">
                             </div>
                             <div class="form-actions">
                                 <button type="submit" class="save-button" :disabled="loading">
                                     {{ editingItem ? 'Update Item' : 'Add Item' }}
                                 </button>
                                  <button type="button" class="cancel-button" @click="cancelEditingItem" v-if="editingItem" :disabled="loading">
                                     Cancel
                                  </button>
                             </div>
                         </form>
                     </div>

                     <table class="site-table item-table">
                         <thead>
                             <tr>
                                 <th>Seq. No.</th>
                                 <th>Instruction</th>
                                 <th>Actions</th> </tr>
                         </thead>
                         <tbody>
                             <tr v-for="(item, index) in instructionItems" :key="item.Id">
                                 <td>{{ item.SafetySeq }}</td> <td>{{ item.SafetyDesc }}</td>
                                 <td>
                                      <button @click="startEditingItem(item)" class="edit-item-button" :disabled="loading">Edit</button>
                                      <button @click="deleteInstructionItem(item.Id)" class="delete-item-button" :disabled="loading">Delete</button>
                                 </td>
                             </tr>
                             <tr v-if="instructionItems.length === 0 && !loading && selectedInstruction">
                                 <td colspan="3" style="text-align: center;">No instruction items found for this instruction.</td>
                             </tr>
                         </tbody>
                     </table>
                 </div>
            </div>
        </div>

        <div v-if="notification.show" :class="['notification', notification.type, { 'fade-out': !notification.show }]">
            {{ notification.message }}.
        </div>

    </div>
</template>

<style scoped>
/* General page layout */
.safety-instruction-page {
    padding: 20px;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    background-color: #f4f6f8;
    min-height: calc(100vh - 40px);
    color: #333;
}

.container {
    display: flex;
    gap: 20px;
    max-width: 1400px; /* Wider container for master-detail */
    margin: 0 auto;
    flex-wrap: wrap;
    align-items: flex-start;
}

/* Panel styling */
.master-panel, .detail-panel {
    background-color: white;
    padding: 25px;
    border-radius: 8px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    min-width: 300px;
    box-sizing: border-box;
    display: flex; /* Use flex for internal layout */
    flex-direction: column; /* Stack content vertically */
    gap: 20px; /* Space between sections within panel */
}

.master-panel {
    flex: 1 1 400px; /* Master panel takes 1 part, basis 400px */
     max-width: 450px; /* Optional max width */
}

.detail-panel {
    flex: 2 1 600px; /* Detail panel takes 2 parts, basis 600px */
    overflow-x: auto;
}

/* Form styling within panels */
.form-panel {
    border: 1px solid #ddd;
    padding: 15px;
    border-radius: 6px;
    background-color: #f9f9f9;
}

h2 {
    color: #333;
    margin-top: 0;
    margin-bottom: 25px;
    border-bottom: 1px solid #eee;
    padding-bottom: 15px;
    font-size: 1.5rem;
    font-weight: 600;
}

h3 {
    color: #555;
    margin-top: 0;
    margin-bottom: 15px;
    font-size: 1.2rem;
    border-bottom: 1px dashed #ccc;
    padding-bottom: 8px;
}


.form-group {
    margin-bottom: 15px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.form-group label {
    display: block;
    color: #555;
    font-weight: bold;
    font-size: 0.95rem;
}

input[type="text"],
input[type="number"],
select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-sizing: border-box;
    font-size: 1rem;
    transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

input[type="text"]:focus,
input[type="number"]:focus,
select:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    outline: none;
}

input[type="text"]:disabled,
input[type="number"]:disabled,
select:disabled {
    background-color: #e9ecef;
    cursor: not-allowed;
    opacity: 0.8;
}

.form-actions {
    margin-top: 20px;
    display: flex;
    gap: 10px;
     flex-wrap: wrap;
}

.save-button,
.cancel-button {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1rem;
    transition: background-color 0.3s ease, opacity 0.3s ease;
     min-width: 90px;
     text-align: center;
     flex-grow: 1;
}

.save-button {
    background-color: #28a745; /* Green */
    color: white;
}

.save-button:hover:not(:disabled) {
    background-color: #218838;
}

.cancel-button {
    background-color: #6c757d; /* Gray */
    color: white;
}

.cancel-button:hover:not(:disabled) {
    background-color: #5a6268;
}

button:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    opacity: 0.6;
}


.table-actions {
    margin-bottom: 15px;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.table-actions button {
    padding: 8px 12px;
    border: 1px solid #007bff;
    border-radius: 4px;
    background-color: #007bff;
    color: white;
    cursor: pointer;
    transition: background-color 0.3s ease, opacity 0.3s ease;
     flex-grow: 1;
     min-width: 60px;
     text-align: center;
}

/* Specific button styles */
.table-actions button:nth-child(2) { /* Edit button */
    background-color: #ffc107;
    border-color: #ffc107;
    color: #212529;
}
.table-actions button:nth-child(2):hover:not(:disabled) {
    background-color: #e0a800;
}

.table-actions button:nth-child(3) { /* Delete button */
    background-color: #dc3545;
    border-color: #dc3545;
    color: white;
}
.table-actions button:nth-child(3):hover:not(:disabled) {
    background-color: #c82333;
}

.table-actions button:hover:not(:disabled) {
    background-color: #0056b3;
    border-color: #0056b3;
}


.site-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
    font-size: 0.95rem;
    border: 1px solid #ddd;
}

.site-table th,
.site-table td {
    padding: 12px;
    border: 1px solid #ddd;
    text-align: left;
    word-break: break-word;
}

.site-table th {
    background-color: #e9ecef;
    font-weight: bold;
    color: #495057;
    text-transform: uppercase;
    font-size: 0.85rem;
}

 .site-table tbody tr:nth-child(even) {
     background-color: #f8f9fa;
 }

.site-table tbody tr:hover {
    background-color: #e9ecef;
    cursor: pointer;
}

.site-table tbody tr.selected {
    background-color: #cfe2ff;
     outline: 2px solid #007bff;
     outline-offset: -2px;
}

/* Item table specific styles */
.item-table th:nth-child(1),
.item-table td:nth-child(1) {
    width: 80px; /* Adjust width for sequence number */
    text-align: center;
}

.item-table td:last-child {
    width: 120px; /* Adjust width for action buttons */
    text-align: center;
}

.edit-item-button, .delete-item-button {
    padding: 5px 10px;
    margin: 0 2px;
    border: 1px solid;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.85rem;
    transition: background-color 0.3s ease, opacity 0.3s ease;
}

.edit-item-button {
    background-color: #ffc107;
    border-color: #ffc107;
    color: #212529;
}
.edit-item-button:hover:not(:disabled) {
    background-color: #e0a800;
}

.delete-item-button {
    background-color: #dc3545;
    border-color: #dc3545;
    color: white;
}
.delete-item-button:hover:not(:disabled) {
    background-color: #c82333;
}


 /* Center text in empty cell */
 .site-table td[colspan="2"],
 .site-table td[colspan="3"] {
     text-align: center;
     font-style: italic;
     color: #666;
 }


/* Responsive adjustments */
@media (max-width: 992px) { /* Adjusted breakpoint for master-detail */
    .container {
        flex-direction: column;
        gap: 15px;
    }

    .master-panel,
    .detail-panel {
        margin-bottom: 15px;
        min-width: unset;
        flex: unset;
        width: 100%;
        padding: 15px;
    }

    h2 {
        margin-bottom: 15px;
        padding-bottom: 10px;
        font-size: 1.3rem;
    }

    .form-group {
        margin-bottom: 15px;
    }

    .form-actions {
        margin-top: 20px;
        flex-direction: column;
        gap: 8px;
    }

    .save-button, .cancel-button {
        width: 100%;
        padding: 10px;
         min-width: unset;
    }

    .table-actions {
        flex-direction: column;
        gap: 8px;
    }

    .table-actions button {
        width: 100%;
        padding: 10px;
         min-width: unset;
    }
}

/* Notification Styling */
.notification {
    position: fixed;
    bottom: 20px;
    left: 20px;
    padding: 15px 20px;
    border-radius: 4px;
    color: white;
    z-index: 1000;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
     opacity: 1;
     transition: opacity 0.5s ease-in-out;
}

 .notification.fade-out {
     opacity: 0;
 }


.notification.success {
    background-color: #28a745;
}

.notification.error {
    background-color: #dc3545;
}

.notification.warning {
    background-color: #ffc107;
    color: #212529;
}

.error-message {
    color: #dc3545;
    margin-bottom: 15px;
    padding: 10px;
    border: 1px solid #dc3545;
    background-color: #fff3f3;
    border-radius: 4px;
     font-size: 0.9rem;
}

</style>
